import React, { useCallback, useMemo } from 'react';
import { ColumnCellTemplateData } from 'devextreme/ui/data_grid';
import { SysIResultados } from '../../../../../types/types.orders';
import { Button } from 'devextreme-react';
import { useAppSelector, useAppDispatch } from '../../../../../contexts/hooks';
import { selectModuleTracking, selectModuleAlarms } from '../../../../../contexts/selectors';
import { resGridConfidential, resGridImageResult, resGridTemplateEmpty, ordPending, agarMicrobiology, multilineIcon, xrayIcon } from '../../../../../images/icons';
import moment from 'moment';
import { showModalTemplates, showModalImages, showModalMicrobiology, showModalMultiline, showModalImagenesDx } from '../../../../../contexts/slices/orders';
import { ResultTypeResult } from '../../../../../types/types.analitos';
import { LazyTooltip } from './LazyOverlays';
import { getAlarmAndAbsurdLabel, isResultAltered } from '../Functions/resultStyles';

function CellComponent({ data }: { data: ColumnCellTemplateData<SysIResultados, number> }): React.ReactElement {
  const { data: rowData, value } = data;

  // Selectores memoizados: el .find() sobre Config_General se calcula una sola
  // vez por cambio de store y se comparte entre todas las celdas.
  const ModuleTracking: boolean = useAppSelector(selectModuleTracking);
  const ModuleAlarms: boolean = useAppSelector(selectModuleAlarms);
  const Prm_Res_Conf = useAppSelector((x) => x.userInfo.Permissions?.Prm_Res_Conf);
  const Prm_ResC = useAppSelector((x) => x.userInfo.Permissions?.Prm_ResC) || false;
  const lstValRefers = useAppSelector((x) => x.configuration.Data?.AnalitosValoresRef);
  const dispatch = useAppDispatch();

  // Función para sanitizar cualquier valor que pueda ser un objeto
  const sanitizeForDisplay = (val: any): string => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  // Clases de estilo derivadas del valor de la fila. Se calculan con useMemo
  // (en lugar de useState + useEffect) para que sigan siendo reactivas a los
  // cambios de valor —fórmulas, resultados que se afectan entre sí— pero SIN
  // provocar el segundo render por celda que causaba el useEffect.
  const alteredData = useMemo(() => (isResultAltered(rowData) ? 'result-error-lbl' : ''), [rowData, value]);
  const alarmAndAbsurdData = useMemo(() => (ModuleAlarms && rowData ? getAlarmAndAbsurdLabel(rowData, lstValRefers) : ''), [rowData, value, ModuleAlarms, lstValRefers]);

  const handleModalTemplate = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalTemplates({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id }));
  }, []);

  const handleModalImage = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalImages({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id }));
  }, []);

  const handleModalMicrobiology = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(
      showModalMicrobiology({
        Visible: true,
        Ord_Id: rowData.Res_Ord_Id,
        Es_Id: rowData.Res_Es_Id,
        Es_Nombre: rowData.Estudio.Es_Nombre,
        An_Id: rowData.Res_An_Id,
        An_Nombre: rowData.Analito.An_Nombre,
        Res_Id: rowData.Res_Id,
        Res_Estado: rowData.Res_Estado,
        Res_Resultado: rowData.Res_Resultado,
        Update: false,
        Pc_Id: rowData.Orden.Ord_Pc_Id,
      })
    );
  }, [rowData, Prm_ResC, dispatch]);

  const handleModalMultiline = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(
      showModalMultiline({
        Visible: true,
        An_Id: rowData.Res_An_Id,
        An_Nombre: rowData.Analito.An_Nombre,
        Res_Id: rowData.Res_Id,
        Res_Estado: rowData.Res_Estado,
        Res_Resultado: rowData.Res_Resultado,
        Update: false,
        Pc_Id: rowData.Orden.Ord_Pc_Id,
      })
    );
  }, [rowData, Prm_ResC, dispatch]);

  const handleModalImagenesDx = useCallback(() => {
    if (!rowData || !Prm_ResC) {
      return;
    }
    dispatch(showModalImagenesDx({ Visible: true, An_Id: rowData.Res_An_Id, An_Nombre: rowData.Analito.An_Nombre, Res_Id: rowData.Res_Id, Res_Estado: rowData.Res_Estado, Res_Resultado: rowData.Res_ResultadoPlantilla, An_Plantilla: rowData.Analito.An_Plantilla, Update: false, Pc_Id: rowData.Orden.Ord_Pc_Id, Ord_Id: rowData.Res_Ord_Id, Es_Id: rowData.Res_Es_Id }));
  }, []);

  //Eliminamos el render primero para las muestras que no están trackeadas
  if ((ModuleTracking && !rowData?.Res_MuestraTrackedada) || !rowData?.Res_EstadoMuestra) {
    return (
      <LazyTooltip position='right' content={!rowData?.Res_EstadoMuestra ? 'Muestra sin confirmar' : 'Muestra sin tracking'}>
        <img src={ordPending} alt='Muestra sin tracking' />
      </LazyTooltip>
    );
  }

  //Validamos los confidenciales
  if (rowData?.Estudio.Es_Confidencial && rowData.Res_Estado > 1 && !Prm_Res_Conf) {
    return (
      <LazyTooltip position='right' content='Resultado confidencial'>
        <img src={resGridConfidential} alt='Muestra confidencial' />
      </LazyTooltip>
    );
  }

  // Protección adicional: si el value es el objeto problemático, manejarlo aquí
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    // Si es un objeto con las claves problemáticas, renderizar como JSON
    const objectKeys = Object.keys(value);
    if (objectKeys.some((key) => key.includes('Superficiales') || key.includes('Parabasales'))) {
      return <div className={`custom-editorText ${alteredData}`}>{JSON.stringify(value)}</div>;
    }
  }

  switch (rowData?.Analito.An_Tr_Id) {
    case ResultTypeResult.NUMERICO: //Numérico
    case ResultTypeResult.FORMULA: //Fórmula
      return <div className={`${alteredData} ${alarmAndAbsurdData}`}>{sanitizeForDisplay(value)}</div>;
    case ResultTypeResult.TEXTUAL: //Textual
    case ResultTypeResult.LISTA: //Lista
    case ResultTypeResult.AUTOCOMPLETAR: //Autocompletar
      return <div className={`custom-editorText ${alteredData} ${alarmAndAbsurdData}`}>{sanitizeForDisplay(value)}</div>;
    case ResultTypeResult.PLANTILLA: //Plantilla
      let result = [];
      let isTemplate = false;
      if (rowData.Res_Resultado) {
        try {
          result = JSON.parse(rowData.Res_Resultado);
          isTemplate = true;
        } catch (error) {
          isTemplate = false;
          result = [];
        }
        if (!isTemplate) {
          return <div className={`custom-editorText ${alteredData}`}>{sanitizeForDisplay(value)}</div>;
        }
      }

      return (
        <LazyTooltip
          position='top'
          content={
            <div>
              {Array.isArray(result) &&
                result.map((x: any, index: number) => {
                  const groupKey = sanitizeForDisplay(x.Group) || `group-${index}`;
                  const groupTitle = sanitizeForDisplay(x.Group) || 'Sin nombre';

                  return React.createElement('div', { key: groupKey, className: 'tooltip-content' }, [
                    React.createElement('h6', { key: `title-${index}`, className: 'tooltip-title' }, groupTitle),
                    ...(Array.isArray(x.Items)
                      ? x.Items.map((y: any, itemIndex: number) => {
                          const itemKey = sanitizeForDisplay(y.Key) || `item-${index}-${itemIndex}`;
                          const itemLabel = sanitizeForDisplay(y.Label) || '';

                          // Si y.Value es un objeto, renderizar cada propiedad
                          if (typeof y.Value === 'object' && y.Value !== null) {
                            return React.createElement('div', { key: itemKey, className: 'tooltip-item' }, [
                              React.createElement('strong', { key: `label-${itemKey}` }, `  ${itemLabel}:`),
                              ...Object.entries(y.Value).map(([key, value], entryIndex: number) => {
                                const entryKey = sanitizeForDisplay(key) || `entry-${index}-${itemIndex}-${entryIndex}`;
                                const entryLabel = sanitizeForDisplay(key) || '';
                                const entryValue = sanitizeForDisplay(value);

                                return React.createElement(
                                  'p',
                                  {
                                    key: entryKey,
                                    style: { marginLeft: '20px', margin: '2px 0', whiteSpace: 'pre-wrap' },
                                  },
                                  [React.createElement('strong', { key: `entry-label-${entryKey}` }, `${entryLabel.trim()}: `), React.createElement('span', { key: `entry-value-${entryKey}`, style: { whiteSpace: 'pre-wrap' } }, entryValue)]
                                );
                              }),
                            ]);
                          } else {
                            // Si y.Value es un string o número, renderizar normalmente
                            const itemValue = sanitizeForDisplay(y.Value);
                            return React.createElement('p', { key: itemKey, className: 'tooltip-item', style: { whiteSpace: 'pre-wrap' } }, [React.createElement('strong', { key: `item-label-${itemKey}` }, `  ${itemLabel}: `), React.createElement('span', { key: `item-value-${itemKey}`, style: { whiteSpace: 'pre-wrap' } }, itemValue)]);
                          }
                        })
                      : []),
                  ]);
                })}
            </div>
          }>
          <Button icon={resGridTemplateEmpty} onClick={handleModalTemplate} />
        </LazyTooltip>
      );
    case ResultTypeResult.FECHA:
      return <div>{value && moment(value).format('YYYY/MM/DD')}</div>;
    case ResultTypeResult.TIEMPO:
      return <div>{value && moment(value).format('HH:mm:SS a')}</div>;
    case ResultTypeResult.IMAGEN:
      return (
        <div>
          <Button icon={resGridImageResult} onClick={handleModalImage} />
        </div>
      );
    case ResultTypeResult.MICROBIOLOGIA:
      return (
        <div>
          <Button icon={agarMicrobiology} onClick={handleModalMicrobiology} disabled={!Prm_ResC} />
        </div>
      );
    case ResultTypeResult.MULTILINEA:
      const textContent = sanitizeForDisplay(value) || '';
      if (!textContent) {
        return (
          <div className={alteredData}>
            <Button icon={multilineIcon} onClick={handleModalMultiline} disabled={!Prm_ResC} />
          </div>
        );
      }
      return (
        <LazyTooltip
          position='top'
          wrapperClassName={alteredData}
          content={
            <div style={{ maxWidth: '600px', maxHeight: '60vh', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'left' }}>
              {textContent}
            </div>
          }>
          <Button icon={multilineIcon} onClick={handleModalMultiline} disabled={!Prm_ResC} />
        </LazyTooltip>
      );
    case ResultTypeResult.IMAGENESDX:
      let resultDx: any[] = [];
      let isTemplateDx = false;
      let parsedDxResult: any = null;
      if (rowData.Res_Resultado) {
        try {
          parsedDxResult = JSON.parse(rowData.Res_Resultado);
          if (parsedDxResult && parsedDxResult.resultado) {
            resultDx = Array.isArray(parsedDxResult.resultado) ? parsedDxResult.resultado : [];
          }
          isTemplateDx = true;
        } catch (error) {
          isTemplateDx = false;
          resultDx = [];
        }
        if (!isTemplateDx) {
          return <div className={`custom-editorText ${alteredData}`}>{sanitizeForDisplay(value)}</div>;
        }
      }
      return (
        <LazyTooltip
          position='top'
          content={
            <div>
              {Array.isArray(resultDx) &&
                resultDx.map((x: any, index: number) => {
                  const groupKey = sanitizeForDisplay(x.Group) || `group-${index}`;
                  const groupTitle = sanitizeForDisplay(x.Group) || 'Sin nombre';
                  return React.createElement('div', { key: groupKey, className: 'tooltip-content' }, [
                    React.createElement('h6', { key: `title-${index}`, className: 'tooltip-title' }, groupTitle),
                    ...(Array.isArray(x.Items)
                      ? x.Items.map((y: any, itemIndex: number) => {
                          const itemKey = sanitizeForDisplay(y.Key) || `item-${index}-${itemIndex}`;
                          const itemLabel = sanitizeForDisplay(y.Label) || '';
                          if (typeof y.Value === 'object' && y.Value !== null) {
                            return React.createElement('div', { key: itemKey, className: 'tooltip-item' }, [
                              React.createElement('strong', { key: `label-${itemKey}` }, `  ${itemLabel}:`),
                              ...Object.entries(y.Value).map(([key, value], entryIndex: number) => {
                                const entryKey = sanitizeForDisplay(key) || `entry-${index}-${itemIndex}-${entryIndex}`;
                                const entryLabel = sanitizeForDisplay(key) || '';
                                const entryValue = sanitizeForDisplay(value);
                                return React.createElement('p', { key: entryKey, style: { marginLeft: '20px', margin: '2px 0', whiteSpace: 'pre-wrap' } }, [
                                  React.createElement('strong', { key: `entry-label-${entryKey}` }, `${entryLabel.trim()}: `),
                                  React.createElement('span', { key: `entry-value-${entryKey}`, style: { whiteSpace: 'pre-wrap' } }, entryValue),
                                ]);
                              }),
                            ]);
                          } else {
                            const itemValue = sanitizeForDisplay(y.Value);
                            return React.createElement('p', { key: itemKey, className: 'tooltip-item', style: { whiteSpace: 'pre-wrap' } }, [
                              React.createElement('strong', { key: `item-label-${itemKey}` }, `  ${itemLabel}: `),
                              React.createElement('span', { key: `item-value-${itemKey}`, style: { whiteSpace: 'pre-wrap' } }, itemValue),
                            ]);
                          }
                        })
                      : []),
                  ]);
                })}
            </div>
          }>
          <Button icon={xrayIcon} onClick={handleModalImagenesDx} />
        </LazyTooltip>
      );
    default:
      return <div>unknown data type: {sanitizeForDisplay(value)}</div>;
  }
}

/**
 * Comparador para React.memo. La celda solo depende del prop `data`; los
 * valores de Redux (permisos, módulos, valores de referencia) disparan su
 * propio re-render vía useAppSelector y no se ven afectados por este memo.
 *
 * Devuelve `true` (omitir render) cuando la fila no cambió en ningún campo que
 * afecte al render. Así se evitan los re-renders que DevExtreme dispara al
 * repintar el grid por selección, scroll o cambios en otras filas. Si cambia
 * el resultado de la celda —o de una fila por una fórmula— `value` /
 * `Res_Resultado` difieren y la celda sí se vuelve a renderizar.
 */
function cellPropsAreEqual(prev: { data: ColumnCellTemplateData<SysIResultados, number> }, next: { data: ColumnCellTemplateData<SysIResultados, number> }): boolean {
  const a = prev.data;
  const b = next.data;
  if (a.value !== b.value) return false;
  const ra = a.data;
  const rb = b.data;
  if (!ra || !rb) return ra === rb;
  return (
    ra.Res_Id === rb.Res_Id &&
    ra.Res_Resultado === rb.Res_Resultado &&
    ra.Res_Estado === rb.Res_Estado &&
    ra.Res_MuestraTrackedada === rb.Res_MuestraTrackedada &&
    ra.Res_EstadoMuestra === rb.Res_EstadoMuestra &&
    ra.Res_ValRefMin === rb.Res_ValRefMin &&
    ra.Res_ValRefMax === rb.Res_ValRefMax
  );
}

export default React.memo(CellComponent, cellPropsAreEqual);
